import { useApolloClient } from '@apollo/client';
import { Button, Dialog, Icon, Label, Loader, Size, Tray } from '@busybusy/webapp-react-ui';
import { MEMBER_LOCATIONS_QUERY } from 'apollo/queries/member-location-queries';
import {
  LOCATION_HISTORY_TIME_ENTRIES_QUERY,
  LOCATION_HISTORY_TIME_ENTRY_QUERY,
} from 'apollo/queries/time-entry-queries';
import { ArrowBackIcon, SettingsIcon } from 'assets/icons';
import project_pin from 'assets/icons/project_pin.svg';
import classNames from 'classnames';
import { HeaderDialog, IconButton } from 'components';
import GoogleMap from 'components/domain/map/GoogleMap/GoogleMap';
import TimeAndSuperscript from 'components/foundation/text/TimeAndSuperscript/TimeAndSuperscript';
import Header from 'components/layout/Header/Header';
import Panel from 'components/layout/Panel/Panel';
import PanelContent from 'components/layout/PanelContent/PanelContent';
import { useApolloPaging, useOpenable, useTimesheetsGraylog } from 'hooks';
import useMemberFeatureSettingsUpdate from 'hooks/models/member/useMemberFeatureSettingsUpdate';
import useMemberSettings from 'hooks/models/member/useMemberSettings';
import { t } from 'i18next';
import { compact, isEmpty, isNil, isNull, sortBy, uniqBy } from 'lodash';
import { DateTime } from 'luxon';
import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { IMember } from 'types';
import ILocation from 'types/Location';
import ILocationHistory from 'types/LocationHistory';
import IMemberLocation from 'types/MemberLocation';
import IProject from 'types/Project';
import ITimeEntry from 'types/TimeEntry';
import MemberLocationType from 'types/enum/MemberLocationType';
import { dateUtils, memberLocationsUtils, stringUtils } from 'utils';
import { TimesheetsTypes } from 'utils/constants/graylogActionTypes';
import { dateTimeFromISOKeepZone } from 'utils/dateUtils';
import { useFeatureFlags } from 'utils/features';
import { shiftPins } from 'utils/locationUtils';
import './LocationHistoryDialog.scss';
import LocationHistorySettingsForm from './LocationHistorySettingsForm/LocationHistorySettingsForm';
import { defaultShowGPSConnections } from './LocationHistorySettingsForm/ShowGPSConnections';
import LocationHistoryTable from './LocationHistoryTable/LocationHistoryTable';
export interface ILocationHistoryDialogProps {
  className?: string | object;
  isOpen: boolean;
  onClose: () => void;
  member: IMember;
  scope: string | DateTime;
}

const LocationHistoryDialog = (props: ILocationHistoryDialogProps) => {
  const { className, isOpen, onClose, member, scope } = props;

  const classes = classNames('location-history-dialog', className);

  const client = useApolloClient();
  const { getAll } = useApolloPaging();
  const isPro = useFeatureFlags('PRO');
  const [timeEntry, setTimeEntry] = useState<ITimeEntry>();
  const [locations, setLocations] = useState<ILocationHistory[]>();
  const [selectedLocation, setSelectedLocation] = useState<string | undefined>();
  const [locationFilter, setLocationFilter] = useState<string>(MemberLocationType.ALL);
  const userEvents = useTimesheetsGraylog();
  const [mapType, setMapType] = useState<string | google.maps.MapTypeId>('roadmap');
  const loaderOpenable = useOpenable();
  const settingsDetails = useOpenable();
  const memberSettings = useMemberSettings();
  const [showConnections, setShowConnections] = useState(
    memberSettings?.web?.features?.showGpsConnections ?? defaultShowGPSConnections.showConnections
  );
  const updateMemberSettings = useMemberFeatureSettingsUpdate();

  useEffect(() => {
    if (isOpen) {
      userEvents.events(TimesheetsTypes.events.action_type.VIEW_GPS);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const setConnections = (value: boolean) => {
    setShowConnections(value);
    updateMemberSettings('showGpsConnections', value);
  };

  async function getData() {
    loaderOpenable.open();
    if (typeof scope === 'string') {
      const entryResults = await client.query<{ timeEntries: ITimeEntry[] }>({
        fetchPolicy: 'network-only',
        query: LOCATION_HISTORY_TIME_ENTRY_QUERY,
        variables: { timeEntryId: scope },
      });
      setTimeEntry(entryResults.data.timeEntries[0]);
      await getLocations([entryResults.data.timeEntries[0]]);
    } else {
      await getLocations(await getTimeEntries());
    }
    loaderOpenable.close();
  }

  async function getLocations(timeEntries: ITimeEntry[]) {
    const newLocations: ILocationHistory[] = [];
    for await (const element of timeEntries) {
      const startTimeWithLocationAllowance = dateTimeFromISOKeepZone(element.startTime)
        .minus({ minute: 3 })
        .toISO({ suppressMilliseconds: true });
      const endTimeWithLocationAllowance = isNil(element.endTime)
        ? undefined
        : dateTimeFromISOKeepZone(element.endTime).plus({ minute: 3 }).toISO({ suppressMilliseconds: true });

      const entryLocationIds: string[] = [];
      if (element.startLocation?.id) {
        entryLocationIds.push(element.startLocation.id);
      }

      if (element.endLocation?.id) {
        entryLocationIds.push(element.endLocation.id);
      }

      const breakStartIds = element.breaks
        .filter((brk) => isNull(brk.deletedOn) && !isNil(brk.startLocation))
        .map((brk) => brk.startLocation!.id);
      const breakEndIds = element.breaks
        .filter((brk) => isNull(brk.deletedOn) && !isNil(brk.endLocation))
        .map((brk) => brk.endLocation!.id);

      const entryLocations = await getAll<IMemberLocation>('memberLocations', {
        query: MEMBER_LOCATIONS_QUERY,
        fetchPolicy: 'network-only',
        variables: {
          first: 500,
          filter: {
            id: {
              contains: [...entryLocationIds, ...breakStartIds, ...breakEndIds],
            },
          },
        },
      });

      const locationResults = await getAll<IMemberLocation>('memberLocations', {
        query: MEMBER_LOCATIONS_QUERY,
        fetchPolicy: 'network-only',
        variables: {
          first: 500,
          filter: {
            memberId: {
              equal: element.memberId,
            },
            timeLocal: {
              greaterThanOrEqual: startTimeWithLocationAllowance,
              lessThanOrEqual: endTimeWithLocationAllowance,
            },
          },
        },
      });
      const uniqLocations = uniqBy([...locationResults, ...entryLocations], 'id');

      uniqLocations.forEach((e) => {
        const locationType = memberLocationsUtils.getType(element, e);
        if (!isPro) {
          if (locationType !== MemberLocationType.LOCATION_UPDATE) {
            const item: ILocationHistory = {
              location: e,
              type: locationType,
              project: element.project,
              timeEntryId: element.id,
            };
            newLocations.push(item);
          }
        } else {
          const item: ILocationHistory = {
            location: e,
            type: locationType,
            project: element.project,
            timeEntryId: element.id,
          };
          newLocations.push(item);
        }
      });
    }
    setLocations(sortBy(newLocations, (lh) => lh.location.timeGmt));
  }

  async function getTimeEntries() {
    const entryResults = await client.query<{ timeEntries: ITimeEntry[] }>({
      query: LOCATION_HISTORY_TIME_ENTRIES_QUERY,
      fetchPolicy: 'network-only',
      variables: {
        first: 100,
        filter: {
          member: { id: { equal: member.id } },
          deletedOn: { isNull: true },
          startTime:
            typeof scope !== 'string'
              ? {
                  lessThanOrEqual: scope.endOf('day').toUTC().toISO({
                    suppressMilliseconds: true,
                    includeOffset: false,
                  }),
                }
              : DateTime.utc().endOf('day').toISO({ suppressMilliseconds: true, includeOffset: false }),
          endTime:
            typeof scope !== 'string'
              ? {
                  greaterThanOrEqual: scope.startOf('day').toUTC().toISO({
                    suppressMilliseconds: true,
                    includeOffset: false,
                  }),
                }
              : DateTime.utc().startOf('day').toISO({ suppressMilliseconds: true, includeOffset: false }),
        },
        sort: [{ startTime: 'asc' }, { createdOn: 'asc' }],
      },
    });
    return entryResults.data.timeEntries.concat(await getOpenTimeEntry());
  }

  async function getOpenTimeEntry() {
    const entryResults = await client.query<{ timeEntries: ITimeEntry[] }>({
      query: LOCATION_HISTORY_TIME_ENTRIES_QUERY,
      fetchPolicy: 'network-only',
      variables: {
        first: 100,
        filter: {
          member: { id: { equal: member.id } },
          deletedOn: { isNull: true },
          startTime:
            typeof scope !== 'string'
              ? {
                  lessThanOrEqual: scope.endOf('day').toUTC().toISO({
                    suppressMilliseconds: true,
                    includeOffset: false,
                  }),
                }
              : DateTime.utc().endOf('day').toISO({ suppressMilliseconds: true, includeOffset: false }),
          endTime: { isNull: true },
        },
        sort: [{ startTime: 'asc' }, { createdOn: 'asc' }],
      },
    });
    return entryResults.data.timeEntries;
  }

  useEffect(() => {
    if (typeof scope === 'string' && isOpen) {
      getData();
    } else if (typeof scope !== 'string' && isOpen) {
      getData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, scope]);

  function renderHeader() {
    return (
      <Header size={Size.GROW}>
        <Tray>
          <Button type="icon" onClick={onClose} className="back-arrow">
            <Icon svg={ArrowBackIcon} />
          </Button>
          <div className="pt-4 pb-4">
            <Label className="header-title pt-1">{stringUtils.getFullNameFromMember(member)}</Label>
            {timeEntry ? renderTimeEntryDateTime(timeEntry) : <></>}
            {typeof scope !== 'string' ? <p>{dateUtils.getDateString(scope, 'ccc, LLL d', true)}</p> : <></>}
          </div>
        </Tray>
        <Tray>
          <IconButton
            onClick={settingsDetails.open}
            svg={SettingsIcon}
            tooltipLabel={t('Settings')}
            buttonSize={Size.MEDIUM}
          />
        </Tray>
      </Header>
    );
  }

  function renderTimeEntryDateTime(entry: ITimeEntry) {
    const startTime = dateUtils.dateTimeFromISOKeepZone(entry.startTime);
    let endTime: DateTime | null = null;
    if (entry.endTime) {
      endTime = dateUtils.dateTimeFromISOKeepZone(entry.endTime);
    }
    return (
      <div>
        {endTime ? (
          <div>
            {dateUtils.getDateString(startTime, 't', false)} - {dateUtils.getDateString(endTime, 't', false)}
          </div>
        ) : (
          <div>{dateUtils.getDateString(startTime, 't', false)} - ?</div>
        )}
        {dateUtils.isSameDay(startTime, endTime ? endTime! : DateTime.local()) ? (
          <div>{dateUtils.getDateString(startTime, 'ccc, LLL d', true)}</div>
        ) : (
          <div>
            {dateUtils.getDateString(startTime, 'ccc, LLL d', true)} -{' '}
            {dateUtils.getDateString(endTime ? endTime! : DateTime.local(), 'ccc, LLL d', true)}
          </div>
        )}
      </div>
    );
  }

  function renderTable() {
    return (
      <LocationHistoryTable
        data={locations ? locations : []}
        onRowClick={handleTableRowClick}
        selectedId={selectedLocation}
        onFilterChange={handleFilterChange}
      />
    );
  }

  function handleFilterChange(filter: string) {
    setLocationFilter(filter);
    userEvents.locationHistoryFilterTypeSet(filter);
  }

  function handleTableRowClick(selectedId?: string) {
    setSelectedLocation(selectedId);
  }

  const mapOptions: google.maps.MapOptions | undefined = useMemo(() => {
    let styles: google.maps.MapTypeStyle[] | undefined = [
      {
        featureType: 'poi',
        elementType: 'labels',
        stylers: [{ visibility: 'off' }],
      },
    ];
    switch (mapType) {
      case 'hybrid':
        styles = undefined;
        break;
    }
    return {
      center: { lat: 39, lng: -95 },
      gestureHandling: 'greedy',
      zoom: 16,
      maxZoom: 20,
      styles,
    };
  }, [mapType]);

  const pins = useMemo(() => {
    if (locations) {
      let newPins = getLocationPins();
      const projectPins = getProjectPins();
      if (!isEmpty(projectPins)) {
        newPins = newPins.concat(projectPins);
      }
      return shiftPins(newPins);
    }
    return [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locations]);

  function renderMap() {
    if (locations) {
      return (
        <GoogleMap
          mapId={'map-' + member.id}
          pinLocations={pins}
          options={mapOptions}
          selectedId={selectedLocation}
          onWindowOpen={setSelectedLocation}
          onWindowClose={handlePinClose}
          onTypeChange={setMapType}
          showRoute={showConnections}
        />
      );
    }
    return <></>;
  }

  const handlePinClose = useCallback(() => {
    setSelectedLocation(undefined);
  }, []);

  function getLocationPins(): ILocation[] {
    if (locations) {
      const filteredLocations =
        locationFilter !== MemberLocationType.ALL
          ? locations.filter((item) => item.type === locationFilter)
          : locations;
      const pins = filteredLocations.map((element) => {
        const item: ILocation = {
          id: element.location.id,
          lat: element.location.latitude,
          lng: element.location.longitude,
          icon: memberLocationsUtils.getTypeIcon(element.type),
          infoWindowTitle: memberLocationsUtils.getTypeTitle(element.type),
          infoWindowBody: getInfoWindowBody(element.location, element.project),
          distance: memberLocationsUtils.getDistanceWhenOutsideProject(element.location, element.project?.projectInfo),
          groupId: element.timeEntryId,
        };
        return item;
      });
      return pins;
    }
    return [];
  }

  function getProjectPins(): ILocation[] {
    if (locations) {
      const filteredLocations = locations.filter((location) => !isNil(location.project));
      const uniqueProjectLocations = uniqBy(filteredLocations, (location) => location.project?.id);
      const pins = uniqueProjectLocations.map((element) => {
        if (
          element.project &&
          element.project.projectInfo &&
          element.project.projectInfo.latitude &&
          element.project.projectInfo.longitude &&
          element.project.projectInfo.locationRadius
        ) {
          const projectPin: ILocation = {
            lat: element.project.projectInfo.latitude,
            lng: element.project.projectInfo.longitude,
            icon: project_pin,
            radius: element.project.projectInfo.locationRadius,
          };
          return projectPin;
        }
      });
      return compact(pins);
    }
    return [];
  }

  function getInfoWindowBody(location: IMemberLocation, project?: IProject | null) {
    const body: ReactNode[] = [
      <p key={location.id}>
        <TimeAndSuperscript
          className="pl-5"
          format={'t'}
          dateTime={dateTimeFromISOKeepZone(location.timeLocal)}
          dst={location.timeDst}
        />
      </p>,
    ];
    if (project) {
      body.push(
        <p className="pl-5" key={project.id}>
          {project.title}
        </p>
      );
    }
    return body;
  }

  function renderContent() {
    return (
      <PanelContent>
        {renderTable()}
        {renderMap()}
      </PanelContent>
    );
  }

  return (
    <Dialog hasDismiss={false} className={classes} size="full" isOpen={isOpen} onClose={onClose}>
      <Panel>
        {renderHeader()}
        {renderContent()}
        <Loader isOpen={loaderOpenable.isOpen} />
        <HeaderDialog title={t('Map Settings')} isOpen={settingsDetails.isOpen} onClose={settingsDetails.close}>
          <LocationHistorySettingsForm
            onSubmit={(value) => {
              settingsDetails.close();
              setConnections(value);
            }}
          />
        </HeaderDialog>
      </Panel>
    </Dialog>
  );
};

export default LocationHistoryDialog;
